import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, onMounted, getCurrentInstance } from "vue";
import axiosBase from "axios";
import { showNotifyDialog } from "@/lib/dialog";
import { Carousel, Slide } from "vue-carousel";
import store from "@/store";
import { User, Novel, NovelFromNolaNovel } from "@/lib/models";
import { evaluateBanner, validateBasicConditions, filterBanners } from "@/lib/utils/banner";
import commerceBannerImage from "@/assets/img/static/banner-commerce-debut.webp";


export default /*#__PURE__*/_defineComponent({
  __name: 'CarouselBanner',
  setup(__props) {

const instance = getCurrentInstance();

const axios = axiosBase.create({
  baseURL: process.env.VUE_APP_MICROCMS_API_ENDPOINT,
  headers: { "X-API-KEY": process.env.VUE_APP_MICROCMS_API_KEY },
});

// microCMSから取得したバナーデータを格納するプロパティ
// microCMS側でプロパティが変更される場合があるため、any型で定義
const banners = ref<any[]>([]);

const novels = computed<Novel[]>(() => store.getters["novelModule/novels"]);
const user = computed<User>(() => store.getters["userModule/user"]);
const novelsFromNolaNovel = computed<NovelFromNolaNovel[]>(() => store.getters["nolaNovelModule/novels"]);
const currentPage = computed(() => instance?.proxy.$route.path);
const hasUsedCommerceBanner = computed(() => store.getters["generalModule/hasUsedCommerceBanner"]);

const getPrioritizedBanners = async (banners: any[]) => {
  // 基本条件に合致しないバナーを除外
  const validBanners = await Promise.all(
    banners.map(async (banner) => ({
      banner,
      isValid: await validateBasicConditions(banner, currentPage.value),
    }))
  );

  const filteredBanners = validBanners.filter((result) => result.isValid).map((result) => result.banner);

  // 条件付きバナーと条件無しバナーを分類
  const conditionalBanners = filteredBanners.filter(
    (banner) => banner.operator?.length > 0 && banner.conditions?.length > 0
  );
  const unconditionalBanners = filteredBanners.filter(
    (banner) => !banner.operator?.length || !banner.conditions?.length
  );

  // 条件付きバナーを評価
  const evaluatedBanners = await Promise.all(
    conditionalBanners.map(async (banner) => ({
      banner,
      isValid: evaluateBanner(banner, novels.value, novelsFromNolaNovel.value, user.value),
    }))
  );

  // 条件にマッチしたバナーを優先して条件無しバナーと統合
  const matchedBanners = evaluatedBanners.filter((result) => result.isValid).map((result) => result.banner);
  return [...matchedBanners, ...unconditionalBanners];
};

const handleCommerceBannerClick = () => {
  if (!hasUsedCommerceBanner.value) {
    instance?.proxy.$router.push("/commerce-debut-guide");
    // 商業道バナーを初めてクリックした場合、利用済みフラグを立てる
    store.dispatch("generalModule/setUsedCommerceBanner", true);
  }
};

const fetchAndProcessBanners = async () => {
  try {
    const bannerResponse = await axios.get(`/bannerweb`);
    if (bannerResponse.status !== 200) {
      await showNotifyDialog({
        title: "エラー",
        content: "バナーの取得に失敗しました。",
      });
      return;
    }
    // 優先して表示するバナーの降順でバナーの配列を取得
    let prioritizedBanners = await getPrioritizedBanners(bannerResponse.data.contents);

    if (hasUsedCommerceBanner.value) {
      // 利用済みフラグが立っている場合、商業道へのバナーを除外
      prioritizedBanners = filterBanners(prioritizedBanners, (banner) => banner.id.includes("banner-commerce-debut"));
    } else {
      const bannerCommerceDebut = {
        id: "banner-commerce-debut",
        image: {
          url: commerceBannerImage,
        },
      };

      // 商業道バナーを配列の先頭に追加
      prioritizedBanners.unshift(bannerCommerceDebut);
    }

    banners.value = prioritizedBanners;
  } catch (error) {
    await showNotifyDialog({
      title: "エラー",
      content: "通信エラーが発生しました。",
    });
    console.error(error);
  }
};

onMounted(async () => {
  await fetchAndProcessBanners();
});

return { __sfc: true,instance, axios, banners, novels, user, novelsFromNolaNovel, currentPage, hasUsedCommerceBanner, getPrioritizedBanners, handleCommerceBannerClick, fetchAndProcessBanners, Carousel, Slide }
}

})